import styled from 'styled-components';

// ===========================================================================
// Estilos locais da listagem de movimentos (2026-09-12, redesenho).
//
// O arquivo antes era código morto (estilos Geração 1 que ninguém importava —
// débito #4 do doc de arquitetura do módulo) e foi reescrito no lugar.
//
// Aqui vive só o que NÃO é variante do kit: o trilho segmentado dos filtros,
// os chips de filtro aplicado, a barra de ferramentas dos resultados e as
// células de duas linhas. Botão, link, tabela, pílula de status e estado vazio
// vêm de `styles/ui/*` e `components/StatusBadge`.
// ===========================================================================

// ---------------------------------------------------------------------------
// Cabeçalho do card de filtros: título + atalhos de período
// ---------------------------------------------------------------------------
export const FilterHeadRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;
   width: 100%;
`;

export const ShortcutsGroup = styled.div`
   display: inline-flex;
   align-items: center;
   gap: 8px;

   .lbl {
      font-size: 12px;
      color: ${({ theme }) => theme.colors.textMuted};
   }
`;

// ---------------------------------------------------------------------------
// Segmented control (Tipo, Situação, atalhos de período)
//
// ⚠️ Armadilha G.4.1: `button:hover` do GlobalStyles (0,0,1,1) vence a classe
// do styled-component (0,0,1,0) — sem declarar `background` no próprio hover,
// o botão pisca no azul do legado. Por isso o hover redeclara o fundo dos dois
// estados (ativo e inativo).
// ---------------------------------------------------------------------------
export const Segmented = styled.div`
   display: inline-flex;
   align-items: center;
   gap: 4px;
   padding: 4px;
   border-radius: 10px;
   background: ${({ theme }) => theme.colors.surfaceMuted};

   button {
      appearance: none;
      border: 0;
      cursor: pointer;
      height: 32px;
      padding: 0 12px;
      border-radius: 8px;
      font-family: inherit;
      font-size: 13px;
      font-weight: 600;
      background: transparent;
      color: ${({ theme }) => theme.colors.textMuted};
      transition:
         background 0.13s ease,
         color 0.13s ease;
      white-space: nowrap;
   }

   button.active {
      background: ${({ theme }) => theme.colors.surface};
      color: ${({ theme }) => theme.colors.link};
      font-weight: 700;
      box-shadow: ${({ theme }) => theme.shadows.sm};
   }

   button:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.surface};
      color: ${({ theme }) => theme.colors.link};
   }

   button:disabled {
      cursor: not-allowed;
      opacity: 0.6;
   }

   button:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.focusRing};
   }
`;

// Agrupa título + metadados à esquerda do cabeçalho de Resultados, deixando a
// barra de ferramentas sozinha na ponta direita (sem isto, o `space-between`
// do SectionHeaderRow joga os metadados para o meio).
export const HeadLeft = styled.div`
   display: flex;
   align-items: baseline;
   gap: 12px;
   flex-wrap: wrap;
   min-width: 0;
`;

// ---------------------------------------------------------------------------
// Rodapé do card de filtros: chips do que está aplicado + ações
// ---------------------------------------------------------------------------
export const FilterFoot = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   flex-wrap: wrap;
   margin-top: 14px;
   padding-top: 12px;
   border-top: 1px solid ${({ theme }) => theme.colors.borderLight};

   .acts {
      display: flex;
      gap: 8px;
      margin-left: auto;
   }
`;

export const ChipList = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   flex-wrap: wrap;
   min-width: 0;
`;

// Chip de filtro APLICADO (não do rascunho): o × refaz a busca sem aquele
// filtro, que é ação explícita do operador — não fere a busca explícita (1.2).
export const Chip = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 4px;
   height: 28px;
   padding: 0 4px 0 10px;
   border-radius: 999px;
   background: ${({ theme }) => theme.colors.primarySoft};
   color: ${({ theme }) => theme.colors.link};
   font-size: 11px;
   font-weight: 800;
   white-space: nowrap;

   button {
      appearance: none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      padding: 0;
      border: 0;
      border-radius: 999px;
      cursor: pointer;
      font-family: inherit;
      font-size: 12px;
      line-height: 1;
      background: rgba(24, 95, 165, 0.12);
      color: inherit;
      transition: background 0.13s ease;
   }

   button:hover:not(:disabled) {
      background: rgba(24, 95, 165, 0.24);
      color: inherit;
   }

   button:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.focusRing};
   }
`;

// ---------------------------------------------------------------------------
// Barra de ferramentas do card de resultados (linhas por página + exportar)
// ---------------------------------------------------------------------------
export const ResultsTools = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   flex-wrap: wrap;

   label {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      color: ${({ theme }) => theme.colors.textMuted};
   }

   select {
      height: 32px;
      min-height: 0;
      padding: 0 8px;
      border-radius: 8px;
      border: 1px solid ${({ theme }) => theme.colors.border};
      background: ${({ theme }) => theme.colors.surface};
      color: ${({ theme }) => theme.colors.textPrimary};
      font-family: inherit;
      font-size: 12.5px;
   }
`;

// ---------------------------------------------------------------------------
// Células
// ---------------------------------------------------------------------------

// Documento e Data ocupam duas linhas (valor + apoio), como no protótipo.
export const TwoLine = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
   line-height: 1.25;

   .sub {
      font-size: 11px;
      color: ${({ theme }) => theme.colors.textMuted};
   }
`;

// Marca o movimento gerado por transferência entre PDVs (handoff 0099 §4.3 —
// campo aditivo `transfer` nos reads). Usa o vínculo real do backend, NUNCA o
// `doc_type 'TRANSFERÊNCIA'`: desde 2026-09-12 o tipo não é mais oferecido nas
// telas de movimento, mas a base tem lançamentos manuais legados com ele —
// inferir pelo tipo marcaria movimento manual como transferência.
export const TransferTag = styled.span`
   display: inline-block;
   margin-top: 3px;
   padding: 1px 6px;
   border-radius: 999px;
   border: 1px solid rgba(24, 95, 165, 0.3);
   background: ${({ theme }) => theme.colors.primarySoft};
   font-size: 10px;
   font-weight: 800;
   color: ${({ theme }) => theme.colors.link};
   white-space: nowrap;
`;

// A coluna "Ações" da linha foi REMOVIDA em 2026-09-12 (decisão do Caio): o
// "ver" era redundante com o duplo clique na linha, e o "imprimir" dispara uma
// requisição lenta (o PDF é gerado por Puppeteer, por request) — ação dessa
// natureza pertence ao detalhe, não a uma listagem de 25 linhas onde um clique
// errado custa caro. Sem consumidor, o `RowActions` saiu junto.
